import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import { FaArrowLeft, FaDownload, FaSpinner } from "react-icons/fa";
import {
  getDiscoveredPagesApi,
  getDomainAllScoresApi,
  getSeoRunDetailsApi,
} from "../../apis/projects.api";
import { calculateScore } from "../../utils/seoScoreCalculator";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";

const SeoResultsPage = () => {
  const { id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const runId = searchParams.get("run_id");
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domainEndpoints, setDomainEndpoints] = useState([]);
  const [pageEndpoints, setPageEndpoints] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]); // Pages user selected
  const [retryCount, setRetryCount] = useState(0);
  const [processingMessage, setProcessingMessage] = useState(
    "Loading SEO results...",
  );
  const [overallScore, setOverallScore] = useState(0);
  const [hasShownResults, setHasShownResults] = useState(false); // Track if we've shown results
  const [downloading, setDownloading] = useState(false);

  const MAX_RETRIES = 24; // 24 retries * 5 seconds = 2 minutes max wait
  const RETRY_INTERVAL = 5000; // 5 seconds

  // Auto-hide loading when we have results
  useEffect(() => {
    if (
      (domainEndpoints.length > 0 || pageEndpoints.length > 0) &&
      !hasShownResults
    ) {
      // console.log('[SEO RESULTS AUTO] Stopping loading because we have data');
      setLoading(false);
      setHasShownResults(true);
    }
  }, [domainEndpoints.length, pageEndpoints.length, hasShownResults]);

  useEffect(() => {
    if (projectId && runId) {
      // First, load selected pages, THEN fetch results
      loadSelectedPages().then((pages) => {
        fetchSeoResults({ preloadedPages: pages });
      });
    } else if (!runId) {
      setError(
        "No run ID provided. Please select a project run to view results.",
      );
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, runId]);

  // Load pages that user selected for analysis
  const loadSelectedPages = async () => {
    try {
      // console.log('[SEO] Loading selected pages for URLs...');
      const response = await getSeoRunDetailsApi(projectId, runId);
      if (response?.data?.page_results) {
        const pages = response.data.page_results.map((p) => ({
          url: p.url,
          page_id: p.page_id,
          _id: p._id, // ✅ Try to extract MongoDB _id if present
          page_title: p.page_title,
          status: p.status || "pending",
          endpoints: [],
          has_results: false,
        }));
        // console.log('[SEO] Loaded selected pages:', pages);
        setSelectedPages(pages);
        return pages; // Return for coordination
      }
    } catch (err) {
      // console.warn('[SEO] Could not preload selected pages:', err.message);
    }

    try {
      // console.log('[SEO] Falling back to discovered pages for selected URLs...');
      const discovered = await getDiscoveredPagesApi(projectId, 1000);
      const allPages = discovered?.data?.pages || [];
      const selected = allPages.filter((page) => page.selected_for_analysis);
      const pages = selected.map((p) => ({
        url: p.url,
        page_id: p.id,
        _id: p._id, // ✅ Try to extract if present
        page_title: p.title,
        status: p.seo_analysis_status || "pending",
        endpoints: [],
        has_results: false,
      }));
      if (pages.length > 0) {
        // console.log('[SEO] Loaded selected pages from discovered list:', pages);
        setSelectedPages(pages);
        return pages;
      }
    } catch (err) {
      // console.warn('[SEO] Could not load selected pages from discovered list:', err.message);
    }
    return [];
  };

  const normalizeScore = (value) => {
    if (value === null || value === undefined) return null;
    const num = typeof value === "number" ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  };

  // Updated to use proper endpoint-specific scoring
  const domainEndpointKeys = new Set([
    "domain_authority",
    "backlink_overview",
    "website_traffic",
    "serp_data",
    "robots_txt",
    "technical_audit",
  ]);

  const getEndpointScore = (metric, metricName = null) => {
    if (!metric || typeof metric !== "object") return 0;

    // Explicitly check for results/status
    const status = metric.status || metric.data?.status || "unknown";
    if (status === "error" || status === "failed") return 0;

    // First try explicitly provided score
    if (metric.score !== undefined && metric.score !== null)
      return normalizeScore(metric.score);

    // Fall back to extracting score fields
    const scoreVal =
      normalizeScore(metric.overall_score) ??
      normalizeScore(metric.score) ??
      normalizeScore(metric.data?.overall_score) ??
      normalizeScore(metric.data?.score) ??
      normalizeScore(metric.result?.overall_score) ??
      normalizeScore(metric.result?.score) ??
      normalizeScore(metric.data?.result?.overall_score) ??
      normalizeScore(metric.data?.result?.score);

    return scoreVal ?? 0;
  };

  // Helper to get consistent icons
  const getIcon = (key) => {
    const icons = {
      seo_content_analysis: "📝",
      technical_audit: "🔧",
      domain_authority: "🏆",
      backlink_overview: "🔗",
      website_traffic: "📈",
      serp_data: "🔍",
      robots_txt: "🤖",
      readability_score: "📖",
      canonical: "🎯",
      headings: "📑",
      helpful_content: "💡",
      keyword_suggestions: "🔑",
      keyword_density: "🎯",
      pwa_audit: "📱",
      image_analysis: "🖼️",
      meta_description: "📋",
      word_count: "📊",
      internal_links: "🔄",
      title_tag: "🏷️",
    };
    return icons[key.toLowerCase()] || "📊";
  };

  // Helper to merge API results into selected pages
  const mergeResultsIntoPages = (apiPageResults, currentSelectedPages) => {
    if (!apiPageResults || apiPageResults.length === 0) {
      return currentSelectedPages;
    }

    const apiResultsMap = {};
    apiPageResults.forEach((page) => {
      apiResultsMap[page.url] = page;
    });

    return currentSelectedPages.map((selectedPage) => {
      const apiResult = apiResultsMap[selectedPage.url];
      if (
        apiResult &&
        apiResult.page_level_results &&
        Object.keys(apiResult.page_level_results).length > 0
      ) {
        // Has results - convert to endpoints using proper scoring
        const endpoints = [];
        Object.keys(apiResult.page_level_results).forEach((key) => {
          const metric = apiResult.page_level_results[key];
          if (metric && typeof metric === "object") {
            // Convert key name to proper format for score calculator
            const normalizedKey = key.toLowerCase().replace(/[-_]/g, "_");
            endpoints.push({
              name: key
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              score: getEndpointScore(metric, normalizedKey),
              status: metric.status || metric.data?.status || "unknown",
              icon: getIcon(normalizedKey),
              data: metric.data || metric,
            });
          }
        });
        return {
          ...selectedPage,
          endpoints: endpoints,
          status: "completed",
          has_results: true,
        };
      }
      // Return as-is if no results yet
      return selectedPage;
    });
  };

  // Helper function to calculate overall score from domain results
  const calculateAndSetOverallScore = (domainResults, pageResults) => {
    const scores = [];

    // Collect all scores from domain endpoints
    if (domainResults && domainResults.length > 0) {
      domainResults.forEach((endpoint) => {
        const score = normalizeScore(endpoint.score);
        if (score !== null) {
          scores.push(score);
        }
      });
    }

    // Collect all scores from page endpoints
    if (pageResults && pageResults.length > 0) {
      pageResults.forEach((page) => {
        if (page.endpoints && page.endpoints.length > 0) {
          page.endpoints.forEach((endpoint) => {
            const score = normalizeScore(endpoint.score);
            if (score !== null) {
              scores.push(score);
            }
          });
        }
      });
    }

    // Calculate average if we have scores
    if (scores.length > 0) {
      const average = Math.round(
        scores.reduce((a, b) => a + b, 0) / scores.length,
      );
      setOverallScore(average);
    } else {
      setOverallScore(0);
    }
  };

  const fetchSeoResults = async ({
    silent = false,
    preloadedPages = null,
  } = {}) => {
    // Double-check runId is not undefined before proceeding
    if (!runId || runId === "undefined") {
      setError("Invalid run ID. Please navigate back and select a valid run.");
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
        // console.log('[SEO RESULTS] Set loading to TRUE (non-silent)');
      } else {
        // console.log('[SEO RESULTS] Silent mode - not setting loading to true');
      }
      setError(null);

      console.log("[SEO RESULTS] Fetching results for:", {
        projectId,
        runId,
        retryCount,
      });

      // Use the run-specific API endpoint
      const response = await getSeoRunDetailsApi(projectId, runId);
      let domainScores = null;
      try {
        const domainScoreResponse = await getDomainAllScoresApi(projectId);
        domainScores = domainScoreResponse?.data?.scores || null;
      } catch (scoreErr) {
        // console.warn('[SEO RESULTS] Could not load domain score summary:', scoreErr.message);
      }

      // console.log('[SEO RESULTS] API Response:', response.data);

      if (response?.data) {
        // The backend returns the full SEO result document
        const data = response.data;

        // console.log('[SEO RESULTS] Checking scores:', {
        //   domain_authority_score: data.domain_level_results?.domain_authority?.score,
        //   domain_authority_overall_score: data.domain_level_results?.domain_authority?.overall_score,
        //   backlink_score: data.domain_level_results?.backlink_overview?.score,
        //   backlink_overall_score: data.domain_level_results?.backlink_overview?.overall_score,
        //   website_traffic_score: data.domain_level_results?.website_traffic?.score,
        //   website_traffic_overall_score: data.domain_level_results?.website_traffic?.overall_score,
        //   serp_score: data.domain_level_results?.serp_data?.score,
        //   serp_overall_score: data.domain_level_results?.serp_data?.overall_score,
        //   robots_score: data.domain_level_results?.robots_txt?.score,
        //   robots_overall_score: data.domain_level_results?.robots_txt?.overall_score,
        //   technical_audit_score: data.domain_level_results?.technical_audit?.score,
        //   technical_audit_overall_score: data.domain_level_results?.technical_audit?.overall_score,
        //   api_overall_score: data.domain_level_results?.overall_score || data.overall_score
        // });

        // Parse the results from the MongoDB document structure
        const domainResults = [];
        const pageResults = [];

        // Handle new API structure with domain_level_results and page_level_results
        if (data.domain_level_results) {
          // Parse domain-level results
          if (data.domain_level_results.domain_authority) {
            domainResults.push({
              name: "Domain Authority",
              score:
                domainScores?.domain_authority?.score ??
                getEndpointScore(
                  data.domain_level_results.domain_authority,
                  "domain_authority",
                ),
              status:
                data.domain_level_results.domain_authority.status ||
                data.domain_level_results.domain_authority.data?.status ||
                "unknown",
              data: data.domain_level_results.domain_authority.data,
            });
          }

          if (data.domain_level_results.backlink_overview) {
            domainResults.push({
              name: "Backlink Overview",
              score:
                domainScores?.backlink_overview?.score ??
                getEndpointScore(
                  data.domain_level_results.backlink_overview,
                  "backlink_overview",
                ),
              status:
                data.domain_level_results.backlink_overview.status ||
                data.domain_level_results.backlink_overview.data?.status ||
                "unknown",
              data: data.domain_level_results.backlink_overview.data,
            });
          }

          if (data.domain_level_results.website_traffic) {
            domainResults.push({
              name: "Website Traffic",
              score:
                domainScores?.website_traffic?.score ??
                getEndpointScore(
                  data.domain_level_results.website_traffic,
                  "website_traffic",
                ),
              status:
                data.domain_level_results.website_traffic.status ||
                data.domain_level_results.website_traffic.data?.status ||
                "unknown",
              data: data.domain_level_results.website_traffic.data,
            });
          }

          if (data.domain_level_results.serp_data) {
            domainResults.push({
              name: "SERP Rankings",
              score:
                domainScores?.serp_data?.score ??
                getEndpointScore(
                  data.domain_level_results.serp_data,
                  "serp_data",
                ),
              status:
                data.domain_level_results.serp_data.status ||
                data.domain_level_results.serp_data.data?.status ||
                "unknown",
              data: data.domain_level_results.serp_data.data,
            });
          }

          if (data.domain_level_results.robots_txt) {
            domainResults.push({
              name: "Robots.txt",
              score:
                domainScores?.robots_txt?.score ??
                getEndpointScore(
                  data.domain_level_results.robots_txt,
                  "robots_txt",
                ),
              status:
                data.domain_level_results.robots_txt.status ||
                data.domain_level_results.robots_txt.data?.status ||
                "unknown",
              data: data.domain_level_results.robots_txt.data,
            });
          }

          if (data.domain_level_results.technical_audit) {
            domainResults.push({
              name: "Technical Audit",
              score:
                domainScores?.technical_audit?.score ??
                getEndpointScore(
                  data.domain_level_results.technical_audit,
                  "technical_audit",
                ),
              status:
                data.domain_level_results.technical_audit.status ||
                data.domain_level_results.technical_audit.data?.status ||
                "unknown",
              data: data.domain_level_results.technical_audit.data,
            });
          }
        }

        // Handle page-level results
        // Only get page results from the page_results array, not from technical_audit
        if (Array.isArray(data.page_results) && data.page_results.length > 0) {
          data.page_results.forEach((page) => {
            if (page.url && page.page_level_results) {
              // Convert page_level_results object into endpoints array
              const endpoints = [];

              // Parse each metric from page_level_results
              Object.keys(page.page_level_results).forEach((key) => {
                const metric = page.page_level_results[key];
                if (metric && typeof metric === "object") {
                  const normalizedKey = key.toLowerCase().replace(/[-_]/g, "_");
                  endpoints.push({
                    name: key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase()),
                    score: getEndpointScore(metric, normalizedKey),
                    status: metric.status || metric.data?.status || "unknown",
                    data: metric.data || metric,
                  });
                }
              });

              pageResults.push({
                url: page.url,
                endpoints: endpoints,
                page_title: page.page_title,
                page_id: page.page_id,
                analyzed_at: page.analyzed_at,
                status: page.status || "unknown",
              });
            }
          });
        }

        // Fallback for old structure (direct fields)
        if (domainResults.length === 0 && pageResults.length === 0) {
          if (data.domain_authority) {
            domainResults.push({
              name: "Domain Authority",
              score: getEndpointScore(data.domain_authority),
              status:
                data.domain_authority.status ||
                data.domain_authority.data?.status ||
                "unknown",
              data: data.domain_authority.data,
            });
          }
        }

        // console.log('[SEO RESULTS] Parsed results:', { domainResults, pageResults });

        setDomainEndpoints(domainResults);
        setPageEndpoints(pageResults);

        // Use preloaded pages if available, otherwise use current selectedPages, otherwise create from API
        const pagesToMerge =
          preloadedPages && preloadedPages.length > 0
            ? preloadedPages
            : selectedPages.length > 0
              ? selectedPages
              : [];

        // Merge API results into selected pages state (keep URLs from selectedPages)
        const mergedPages = mergeResultsIntoPages(
          data.page_results,
          pagesToMerge,
        );
        // console.log('[SEO RESULTS] Merged pages with URLs:', mergedPages);
        setSelectedPages(mergedPages);

        // Calculate overall score from collected endpoints
        calculateAndSetOverallScore(domainResults, mergedPages);

        // Check if we have actual results
        const hasResults =
          domainResults.length > 0 ||
          pageResults.length > 0 ||
          mergedPages.some((p) => p.has_results);

        console.log("[SEO RESULTS] Results check:", {
          hasResults,
          domainResultsCount: domainResults.length,
          pageResultsCount: pageResults.length,
          mergedPagesCount: mergedPages.length,
          status: data.status,
        });

        if (
          hasResults ||
          data.status === "partial" ||
          data.status === "completed" ||
          data.status === "ok" ||
          data.status === "success"
        ) {
          // We have results or analysis is done (partial/completed) - display what we have
          // console.log('[SEO RESULTS] Displaying results, stopping loading');
          // console.log('[SEO RESULTS] About to setLoading(false)');
          setLoading(false);
          // console.log('[SEO RESULTS] setLoading(false) called');
        } else if (
          data.status === "processing" ||
          data.status === "pending" ||
          data.status === "accepted"
        ) {
          // Analysis still processing and no results yet - retry
          if (retryCount < MAX_RETRIES) {
            const waitTime = Math.round(
              ((retryCount + 1) * RETRY_INTERVAL) / 1000,
            );
            setProcessingMessage(
              `SEO analysis is processing... (${waitTime}s)`,
            );
            // console.log('[SEO RESULTS] Still processing, will retry...');

            setTimeout(() => {
              setRetryCount((prev) => prev + 1);
              fetchSeoResults({ silent: true });
            }, RETRY_INTERVAL);
          } else {
            // console.log('[SEO RESULTS] Max retries reached, stopping');
            setLoading(false);
          }
        } else {
          // Any other status - just display what we have
          // console.log('[SEO RESULTS] Other status, displaying results');
          setLoading(false);
        }
      } else {
        setError("No data received from server");
        setLoading(false);
      }
    } catch (err) {
      // console.error('[SEO RESULTS] Error fetching SEO results:', err);
      // console.error('[SEO RESULTS] Error details:', {
      //   status: err.response?.status,
      //   message: err.response?.data?.message || err.message,
      //   retryCount
      // });

      // If it's a 404 or "not found" error, retry (analysis might still be processing)
      if (
        err.response?.status === 404 ||
        err.response?.data?.message?.includes("not found")
      ) {
        if (retryCount < MAX_RETRIES) {
          const waitTime = Math.round(
            ((retryCount + 1) * RETRY_INTERVAL) / 1000,
          );
          setProcessingMessage(`SEO analysis is processing... (${waitTime}s)`);
          // console.log('[SEO RESULTS] 404 error, will retry...');

          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
            fetchSeoResults({ silent: true });
          }, RETRY_INTERVAL);
          return;
        } else {
          // console.log('[SEO RESULTS] Max retries reached on 404, stopping');
        }
      }

      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load SEO results. Please try again later.",
      );
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return isDarkMode ? "bg-green-900/30" : "bg-green-50";
    if (score >= 60) return isDarkMode ? "bg-yellow-900/30" : "bg-yellow-50";
    return isDarkMode ? "bg-red-900/30" : "bg-red-50";
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      // Get domain name from selected pages or page endpoints
      let domainName = "SEO_Report";

      try {
        if (selectedPages.length > 0 && selectedPages[0]) {
          const url = selectedPages[0];
          if (url.startsWith("http://") || url.startsWith("https://")) {
            domainName = new URL(url).hostname
              .replace(/^www\./, "")
              .replace(/\./g, "");
          } else {
            domainName = url.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
          }
        } else if (pageEndpoints.length > 0 && pageEndpoints[0]?.page_url) {
          const url = pageEndpoints[0].page_url;
          if (url.startsWith("http://") || url.startsWith("https://")) {
            domainName = new URL(url).hostname
              .replace(/^www\./, "")
              .replace(/\./g, "");
          } else {
            domainName = url.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
          }
        } else {
          domainName = `SEO_Report_${projectId}`;
        }
      } catch (urlError) {
        // console.warn('URL parsing failed, using fallback:', urlError);
        domainName = `SEO_Report_${projectId || "Analysis"}`;
      }

      const fileName = `SEO_Report_${domainName}.pdf`;

      // Create PDF content
      const element = document.createElement("div");
      element.style.padding = "20px";
      element.style.fontFamily = "Arial, sans-serif";
      element.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #333; margin: 0 0 10px 0;">SEO Analysis Report</h1>
          <p style="color: #666; margin: 0;">${
            selectedPages.length > 0
              ? selectedPages.join(", ")
              : pageEndpoints.length > 0
                ? pageEndpoints.map((p) => p.page_url).join(", ")
                : "Domain Analysis"
          }</p>
          <p style="color: #999; font-size: 12px; margin-top: 5px;">Generated on ${new Date().toLocaleDateString()}</p>
        </div>

        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin-bottom: 30px;">
          <h2 style="color: #333; margin: 0 0 15px 0;">Overall SEO Score</h2>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div>
              <p style="color: #666; margin: 0 0 10px 0;"><strong>Overall Score:</strong></p>
              <p style="font-size: 48px; color: #${overallScore >= 80 ? "10b981" : overallScore >= 60 ? "f59e0b" : "ef4444"}; margin: 0; font-weight: bold;">${overallScore}%</p>
              <p style="color: #999; margin: 5px 0 0 0; font-size: 12px;">Out of 100</p>
            </div>
            <div>
              <p style="color: #666; margin: 0 0 10px 0;"><strong>Status:</strong></p>
              <p style="font-size: 18px; color: #333; margin: 0; font-weight: bold;">${overallScore >= 80 ? "Excellent" : overallScore >= 60 ? "Good" : "Needs Improvement"}</p>
              <p style="color: #999; margin: 5px 0 0 0; font-size: 12px;">SEO Performance</p>
            </div>
          </div>
        </div>

        ${
          domainEndpoints.length > 0
            ? `
          <div style="margin-bottom: 30px;">
            <h2 style="color: #333; margin: 0 0 15px 0;">Domain-Level Analysis</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left; color: #333;">Endpoint</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: center; color: #333;">Score</th>
                </tr>
              </thead>
              <tbody>
                ${domainEndpoints
                  .map(
                    (ep) => `
                  <tr>
                    <td style="border: 1px solid #ddd; padding: 10px; color: #333;">${ep.name}</td>
                    <td style="border: 1px solid #ddd; padding: 10px; text-align: center; color: #${ep.score >= 80 ? "10b981" : ep.score >= 60 ? "f59e0b" : "ef4444"}; font-weight: bold;">${ep.score || "N/A"}%</td>
                  </tr>
                `,
                  )
                  .join("")}
              </tbody>
            </table>
          </div>
        `
            : ""
        }

        ${
          pageEndpoints.length > 0
            ? `
          <div style="margin-bottom: 30px;">
            <h2 style="color: #333; margin: 0 0 15px 0;">Page-Level Analysis (${pageEndpoints.length} pages)</h2>
            ${pageEndpoints
              .map(
                (page) => `
              <div style="margin-bottom: 20px; page-break-inside: avoid;">
                <h3 style="color: #555; margin: 0 0 10px 0;">${page.page_url}</h3>
                <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
                  <thead>
                    <tr style="background-color: #f0f0f0;">
                      <th style="border: 1px solid #ddd; padding: 8px; text-align: left; color: #333;">Endpoint</th>
                      <th style="border: 1px solid #ddd; padding: 8px; text-align: center; color: #333;">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${(page.endpoints || [])
                      .map(
                        (ep) => `
                      <tr>
                        <td style="border: 1px solid #ddd; padding: 8px; color: #333;">${ep.name}</td>
                        <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: #${ep.score >= 80 ? "10b981" : ep.score >= 60 ? "f59e0b" : "ef4444"}; font-weight: bold;">${ep.score || "N/A"}%</td>
                      </tr>
                    `,
                      )
                      .join("")}
                  </tbody>
                </table>
              </div>
            `,
              )
              .join("")}
          </div>
        `
            : ""
        }

        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #999; font-size: 11px; text-align: center;">
          <p style="margin: 0;">This report was automatically generated by SEO Analysis Tool</p>
          <p style="margin: 5px 0 0 0;">For more information, visit your dashboard</p>
        </div>
      `;

      // PDF options
      const options = {
        margin: 10,
        filename: fileName,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { orientation: "portrait", unit: "mm", format: "a4" },
      };

      // Generate and download PDF
      html2pdf().set(options).from(element).save();

      toast.success(`Report downloaded: ${fileName}`);
    } catch (error) {
      // console.error('Error downloading report:', error);
      toast.error(
        "Failed to download report: " + (error.message || "Unknown error"),
      );
    } finally {
      setDownloading(false);
    }
  };

  // console.log('[SEO RESULTS RENDER] Current loading state:', loading, 'domainEndpoints:', domainEndpoints.length, 'pageEndpoints:', pageEndpoints.length, 'hasShownResults:', hasShownResults, 'error:', error);

  if (loading) {
    return (
      <div
        className={`min-h-screen p-3 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="max-w-6xl mx-auto">
          {/* Header Skeleton */}
          <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg animate-pulse ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
            ></div>
            <div className="flex-1 min-w-0">
              <div
                className={`h-6 sm:h-8 w-48 sm:w-64 rounded animate-pulse mb-2 ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
              ></div>
              <div
                className={`h-3 sm:h-4 w-32 sm:w-48 rounded animate-pulse ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
              ></div>
            </div>
          </div>

          {/* Loading Message */}
          <div
            className={`p-4 sm:p-8 rounded-lg shadow-lg text-center mb-4 sm:mb-8 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="flex flex-col items-center justify-center">
              <div className="relative mb-2 sm:mb-4">
                <div className="animate-spin rounded-full h-12 sm:h-20 w-12 sm:w-20 border-b-4 border-blue-600"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg sm:text-2xl">🔍</span>
                </div>
              </div>
              <h2
                className={`text-lg sm:text-2xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Analyzing Your Website...
              </h2>
              <p
                className={`text-sm sm:text-lg ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {processingMessage}
              </p>
              <p
                className={`text-xs sm:text-sm mt-1 sm:mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
              >
                This may take a few moments. Please wait...
              </p>
            </div>
          </div>

          {/* Overall Score Skeleton */}
          <div
            className={`p-3 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-8 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex-1 w-full sm:w-auto">
                <div
                  className={`h-6 sm:h-8 w-36 sm:w-48 rounded animate-pulse mb-2 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                ></div>
                <div
                  className={`h-3 sm:h-4 w-40 sm:w-64 rounded animate-pulse ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                ></div>
              </div>
              <div
                className={`w-28 h-28 sm:w-40 sm:h-40 rounded-full animate-pulse flex-shrink-0 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
              ></div>
            </div>
          </div>

          {/* Endpoints Skeleton */}
          <div
            className={`p-3 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div
              className={`h-5 sm:h-6 w-32 sm:w-48 rounded animate-pulse mb-3 sm:mb-4 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
            ></div>
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`p-2 sm:p-4 rounded-lg ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div
                      className={`h-4 sm:h-5 w-24 sm:w-32 rounded animate-pulse ${isDarkMode ? "bg-gray-600" : "bg-gray-200"}`}
                    ></div>
                    <div
                      className={`h-6 sm:h-8 w-12 sm:w-16 rounded animate-pulse ${isDarkMode ? "bg-gray-600" : "bg-gray-200"}`}
                    ></div>
                  </div>
                  <div
                    className={`h-3 sm:h-4 w-full rounded animate-pulse ${isDarkMode ? "bg-gray-600" : "bg-gray-200"}`}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate("/dashboard")}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
            >
              <FaArrowLeft
                className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              />
            </button>
            <h1
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              SEO Analysis Results
            </h1>
          </div>

          <div
            className={`p-8 rounded-lg shadow-lg text-center ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="text-6xl mb-4">❌</div>
            <h2
              className={`text-2xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Error Loading Results
            </h2>
            <p
              className={`text-lg mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              {error}
            </p>
            <div className="space-y-3">
              <button
                onClick={fetchSeoResults}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Retry
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ml-3 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-900"}`}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!domainEndpoints.length && !pageEndpoints.length) {
    return (
      <div
        className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate("/dashboard")}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
            >
              <FaArrowLeft
                className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              />
            </button>
            <h1
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              SEO Analysis Results
            </h1>
          </div>

          <div
            className={`p-8 rounded-lg shadow-lg text-center ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="text-6xl mb-4">⏱️</div>
            <h2
              className={`text-2xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Analysis Still Processing
            </h2>
            <p
              className={`text-lg mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Your SEO analysis is taking longer than expected. The backend is
              still processing your website data.
            </p>
            <p
              className={`text-sm mb-6 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
            >
              Please try again in a few minutes, or check back later.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={fetchSeoResults}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Check Again
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                }`}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <div className="max-w-6xl mx-auto">
        {processingMessage && loading && (
          <div
            className={`mb-6 p-4 rounded-lg text-sm ${isDarkMode ? "bg-gray-800 text-gray-300" : "bg-blue-50 text-blue-700"}`}
          >
            {processingMessage}
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
            >
              <FaArrowLeft
                className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              />
            </button>
            <div>
              <h1
                className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                SEO Analysis Results
              </h1>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Run ID: {runId}
              </p>
            </div>
          </div>
        </div>

        {/* Overall Score */}
        <div
          className={`p-6 rounded-lg shadow-lg mb-8 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-6">
            <div>
              <h2
                className={`text-2xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Overall SEO Score
              </h2>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Based on{" "}
                {domainEndpoints.filter(
                  (e) => e.score !== null && e.score !== undefined,
                ).length +
                  pageEndpoints.reduce((total, page) => {
                    return (
                      total +
                      (page.endpoints || []).filter(
                        (e) => e.score !== null && e.score !== undefined,
                      ).length
                    );
                  }, 0)}{" "}
                analyzed endpoints
              </p>
            </div>
            <div
              className={`text-center p-8 rounded-full ${getScoreBgColor(overallScore)}`}
            >
              <div
                className={`text-6xl font-bold ${getScoreColor(overallScore)}`}
              >
                {overallScore}%
              </div>
            </div>
          </div>

          {/* Download Report Button */}
          <button
            onClick={downloadReport}
            disabled={downloading}
            className={`w-full py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
              downloading
                ? "bg-gray-400 cursor-not-allowed text-gray-600"
                : isDarkMode
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
            }`}
          >
            {downloading ? (
              <>
                <FaSpinner className="animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <FaDownload />
                Download Full Report
              </>
            )}
          </button>
        </div>

        {/* Domain-Level Endpoints */}
        {domainEndpoints.length > 0 && (
          <div className="mb-8">
            <h3
              className={`text-2xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Domain-Level Analysis
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {domainEndpoints.map((endpoint) => (
                <div
                  key={endpoint.name}
                  className={`p-5 rounded-lg shadow hover:shadow-lg transition-shadow ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{endpoint.icon || "📊"}</span>
                      <h4
                        className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {endpoint.name}
                      </h4>
                    </div>
                    {endpoint.score !== null && endpoint.score !== undefined ? (
                      <div
                        className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreBgColor(endpoint.score)} ${getScoreColor(endpoint.score)}`}
                      >
                        {endpoint.score}%
                      </div>
                    ) : (
                      <div
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"}`}
                      >
                        {endpoint.status === "completed"
                          ? "No score"
                          : "Pending"}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() =>
                      navigate(
                        `/dashboard/seo-endpoint-detail/${projectId}/${encodeURIComponent(endpoint.name)}`,
                        {
                          state: { endpoint, runId, projectId },
                        },
                      )
                    }
                    className="w-full mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Page-Level Endpoints */}
        {(selectedPages.length > 0 || pageEndpoints.length > 0) && (
          <div>
            <h3
              className={`text-2xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Page-Level Analysis
            </h3>
            <div className="space-y-3">
              {(selectedPages.length > 0 ? selectedPages : pageEndpoints).map(
                (page, pageIdx) => (
                  <div
                    key={page.page_id || page.url || pageIdx}
                    className={`p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center justify-between ${
                      isDarkMode ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <div>
                      <p
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {page.url}
                      </p>
                      {page.page_title && (
                        <p
                          className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {page.page_title}
                        </p>
                      )}
                    </div>
                    <button
                      disabled={
                        !page.has_results &&
                        (page.status === "pending" ||
                          page.status === "in_progress" ||
                          (page.endpoints || []).length === 0)
                      }
                      onClick={() =>
                        navigate(`/dashboard/seo-page-results/${projectId}`, {
                          state: {
                            page: page,
                            runId: runId,
                            projectId: projectId,
                          },
                        })
                      }
                      className={`px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ml-4 ${
                        !page.has_results &&
                        (page.status === "pending" ||
                          page.status === "in_progress" ||
                          (page.endpoints || []).length === 0)
                          ? isDarkMode
                            ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      {!page.has_results &&
                      (page.status === "pending" ||
                        page.status === "in_progress" ||
                        (page.endpoints || []).length === 0)
                        ? "Loading..."
                        : "View Details"}
                    </button>
                  </div>
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeoResultsPage;
